/**
 * Simple in-memory cache for PAMSSAC and CI score results
 * Reduces rate-limit hits by caching recent score calculations
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

class ScoreCache {
  private cache: Map<string, CacheEntry<any>>;
  private defaultTTL: number; // Time to live in milliseconds

  constructor(defaultTTL: number = 5 * 60 * 1000) { // Default 5 minutes
    this.cache = new Map();
    this.defaultTTL = defaultTTL;
  }

  /**
   * Generate cache key from parameters
   */
  private generateKey(type: 'ci' | 'pamssac', userId: string, contactId: string): string {
    return `${type}:${userId}:${contactId}`;
  }

  /**
   * Get cached value if it exists and hasn't expired
   */
  get<T>(type: 'ci' | 'pamssac', userId: string, contactId: string): T | null {
    const key = this.generateKey(type, userId, contactId);
    const entry = this.cache.get(key);

    if (!entry) {
      return null;
    }

    // Check if expired
    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return null;
    }

    return entry.data as T;
  }

  /**
   * Set cache value with optional custom TTL
   */
  set<T>(
    type: 'ci' | 'pamssac',
    userId: string,
    contactId: string,
    data: T,
    ttl?: number
  ): void {
    const key = this.generateKey(type, userId, contactId);
    const now = Date.now();
    const expiresAt = now + (ttl || this.defaultTTL);

    this.cache.set(key, {
      data,
      timestamp: now,
      expiresAt,
    });
  }

  /**
   * Invalidate specific cache entry
   */
  invalidate(type: 'ci' | 'pamssac', userId: string, contactId: string): void {
    const key = this.generateKey(type, userId, contactId);
    this.cache.delete(key);
  }

  /**
   * Invalidate all cache entries for a user
   */
  invalidateUser(userId: string): void {
    const keysToDelete: string[] = [];
    
    for (const key of this.cache.keys()) {
      if (key.includes(`:${userId}:`)) {
        keysToDelete.push(key);
      }
    }
    
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Remove expired entries (garbage collection)
   */
  cleanup(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];

    for (const [key, entry] of this.cache.entries()) {
      if (now > entry.expiresAt) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Get cache statistics
   */
  getStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
    };
  }
}

// Export singleton instance
export const scoreCache = new ScoreCache(5 * 60 * 1000); // 5 minute TTL

// Auto-cleanup every 10 minutes
if (typeof window !== 'undefined') {
  setInterval(() => {
    scoreCache.cleanup();
  }, 10 * 60 * 1000);
}
